import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowUpDown } from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MarketplaceDealBreakdown = ({ token, filters, revenueVisible = true }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("spend");
  const [sortDir, setSortDir] = useState("desc");

  const fetchBreakdown = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.ssp_source) params.ssp_source = filters.ssp_source;
      if (filters.dsp) params.dsp = filters.dsp;
      if (filters.deal_name) params.deal_name = filters.deal_name;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.tag_ids && filters.tag_ids.length) params.tag_ids = filters.tag_ids.join(",");
      const res = await axios.get(`${API}/reporting/marketplace/breakdowns/deal_name`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data || []);
    } catch {
      toast.error("Failed to load deal breakdown");
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { fetchBreakdown(); }, [fetchBreakdown]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sorted = [...data].sort((a, b) => {
    const av = a[sortKey] ?? "";
    const bv = b[sortKey] ?? "";
    const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const allColumns = [
    { key: "label", label: "Deal Name", w: "min-w-[250px]" },
    { key: "impressions", label: "Impressions", num: true },
    { key: "clicks", label: "Clicks", num: true },
    { key: "spend", label: "Spend", num: true, prefix: "$" },
    { key: "revenue", label: "Revenue", num: true, prefix: "$", requireRevenue: true },
    { key: "margin_pct", label: "Margin %", num: true, suffix: "%", requireRevenue: true },
    { key: "cpm", label: "CPM", num: true, prefix: "$" },
    { key: "ctr", label: "CTR %", num: true, suffix: "%" },
  ];

  const columns = allColumns.filter((c) => !c.requireRevenue || revenueVisible);

  const fmtVal = (val, col) => {
    if (val == null || val === "" || val === "(empty)") return col.key === "label" ? "(no deal name)" : "-";
    if (col.num) {
      const n = Number(val);
      const formatted = col.key === "impressions" || col.key === "clicks"
        ? n.toLocaleString()
        : n.toFixed(2);
      return `${col.prefix || ""}${formatted}${col.suffix || ""}`;
    }
    return String(val);
  };

  // Compute totals
  const totals = data.reduce((acc, r) => {
    acc.impressions += r.impressions || 0;
    acc.clicks += r.clicks || 0;
    acc.spend += r.spend || 0;
    acc.revenue += r.revenue || 0;
    return acc;
  }, { impressions: 0, clicks: 0, spend: 0, revenue: 0 });
  totals.margin_pct = totals.spend > 0 ? +(totals.revenue / totals.spend * 100).toFixed(2) : 0;
  totals.cpm = totals.impressions > 0 ? +(totals.spend / totals.impressions * 1000).toFixed(2) : 0;
  totals.ctr = totals.impressions > 0 ? +(totals.clicks / totals.impressions * 100).toFixed(4) : 0;

  return (
    <Card className="bg-[#1e2124] border-[#2a2d31]" data-testid="deal-breakdown-table">
      <CardContent className="p-0">
        <div className="px-4 py-3 border-b border-[#2a2d31] flex items-center justify-between">
          <p className="text-[#ebebeb]/60 text-xs font-mono uppercase tracking-wider">By Deal Name</p>
          <span className="text-[#ebebeb]/30 text-xs font-mono">{data.length} deals</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="w-5 h-5 text-[#ff3737] animate-spin" />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center p-8">
            <p className="text-[#ebebeb]/40 font-mono text-xs">No data</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-[#1e2124] z-10">
                <TableRow className="border-[#2a2d31] hover:bg-transparent">
                  {columns.map((col) => (
                    <TableHead
                      key={col.key}
                      className={`text-[#ebebeb]/50 font-mono text-xs cursor-pointer hover:text-[#ebebeb]/80 ${col.w || ""}`}
                      onClick={() => handleSort(col.key)}
                    >
                      <span className="flex items-center gap-1">
                        {col.label}
                        {sortKey === col.key && <ArrowUpDown className="w-3 h-3 text-[#ff3737]" />}
                      </span>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Totals row */}
                <TableRow className="border-[#2a2d31] bg-white/[0.03] font-semibold">
                  {columns.map((col) => (
                    <TableCell key={col.key} className="font-mono text-xs text-white">
                      {col.key === "label" ? "TOTAL" : fmtVal(totals[col.key], col)}
                    </TableCell>
                  ))}
                </TableRow>
                {sorted.map((row, i) => (
                  <TableRow key={i} className="border-[#2a2d31] hover:bg-white/[0.03]">
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={`font-mono text-xs ${col.key === "label" ? "text-[#ebebeb]/90 max-w-[300px] truncate" : "text-[#ebebeb]/70"}`}
                        title={col.key === "label" ? row.label : undefined}
                      >
                        {fmtVal(row[col.key], col)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MarketplaceDealBreakdown;
